import { lazy } from "react";

/**
 * Every route in the product, each tagged with the module it belongs to.
 *
 * The original App.js decided access with nested `user?.role === "Admin"` blocks
 * and a few `user?.userId === 41` exceptions. Here a route declares its module
 * and <ProtectedRoute> asks the server-issued permission set. Moving a screen
 * between roles is then a tick-box in System > Role Permissions.
 *
 *   path       React Router path
 *   module     Module_Master.module_code, or null for "any signed-in user"
 *   permission verb required to open it (default VIEW)
 *
 * Secondary screens (edit/fill forms reached from a list) are tagged with their
 * parent module and the verb they actually perform, so granting VIEW alone does
 * not hand out the edit forms.
 */

// ---- Dashboards -----------------------------------------------------------
const Dashboard = lazy(() => import("../Pages/Dashboard"));
const DashboardAdmin = lazy(() => import("../Pages/DashboardAdmin"));
const DashboardAccount = lazy(() => import("../Pages/DashboardAccount"));

// ---- Expenses -------------------------------------------------------------
const UserVoucherFill = lazy(() => import("../Pages/UserVoucherFill"));
const UserVoucherMulti = lazy(() => import("../Pages/UserVoucherMulti"));
const UserLocalConveyanceVoucher = lazy(() => import("../Pages/UserLocalConveyanceVoucher"));
const ExpenseDeclaration = lazy(() => import("../Pages/ExpenseDeclaration"));
const ExpenseForm = lazy(() => import("../Pages/ExpenseForm"));
const ExpenseRequestAdmin = lazy(() => import("../Pages/ExpenseRequestAdmin"));
const ExpenseRequestAdminList = lazy(() => import("../Pages/ExpenseRequestAdminList"));
const ExpenseRequestAccountant = lazy(() => import("../Pages/ExpenseRequestAccountant"));
const ExpenseRequestAccountantnew = lazy(() => import("../Pages/ExpenseRequestAccountantnew"));
const ExpenseRequestAccountantbatch = lazy(() => import("../Pages/ExpenseRequestAccountantbatch"));
const ExpenseRequestAccountantbatchlist = lazy(() => import("../Pages/ExpenseRequestAccountantbatchlist"));
const ExpenseRequestHR = lazy(() => import("../Pages/ExpenseRequestHR"));
const AdminVoucherEditByUser = lazy(() => import("../Pages/AdminVoucherEditByUser"));
const RevokeEditVoucher = lazy(() => import("../Pages/RevokeEditVoucher"));
const PendingVoucher = lazy(() => import("../Pages/PendingVoucher"));
const AcceptedVoucher = lazy(() => import("../Pages/AcceptedVoucher"));
const RejectedVoucher = lazy(() => import("../Pages/RejectedVoucher"));
const CompletedExpense = lazy(() => import("../Pages/CompletedExpense"));
const OfficialHistory = lazy(() => import("../Pages/OfficialHistory"));
const AdminHistoryOff = lazy(() => import("../Pages/AdminHistoryOff"));
const VoucherFullDetail = lazy(() => import("../Pages/VoucherFullDetail"));
const VoucherDetails = lazy(() => import("../Pages/VoucherDetails"));
const AdminVoucherFullDetail = lazy(() => import("../Pages/AdminVoucherFullDetail"));
const AccountantVoucherFullDetail = lazy(() => import("../Pages/AccountantVoucherFullDetail"));
const AdminVoucherDetailsAll = lazy(() => import("../Pages/AdminVoucherDetailsAll"));
const AdminVoucherHistoryDetails = lazy(() => import("../Pages/AdminVoucherHistoryDetails"));
const ApprovedAdminVoucherdetails = lazy(() => import("../Pages/ApprovedAdminVoucherdetails"));

// ---- Expense edit / fill forms --------------------------------------------
const EditVoucher = lazy(() => import("../Pages/EditVoucher"));
const EditVoucherAccountant = lazy(() => import("../Pages/EditVoucherAccountant"));
const EditMultiVoucher = lazy(() => import("../Pages/EditMultiVoucher"));
const EditLocalConveyanceVoucher = lazy(() => import("../Pages/EditLocalConveyanceVoucher"));
const AdminEditVoucher = lazy(() => import("../Pages/AdminEditVoucher"));
const AdminUpdateVoucherDetails = lazy(() => import("../Pages/AdminUpdateVoucherDetails"));
const AdminUpdateMultiVoucher = lazy(() => import("../Pages/AdminUpdateMultiVoucher"));
const AdminUpdateLocalConveyanceVoucher = lazy(() => import("../Pages/AdminUpdateLocalConveyanceVoucher"));
const AdminVoucerDetailsFill = lazy(() => import("../Pages/AdminVoucerDetailsFill"));
const AdminMultiVoucherFill = lazy(() => import("../Pages/AdminMultiVoucherFill"));
const AdminMultiVoucherByAcc = lazy(() => import("../Pages/AdminMultiVoucherByAcc"));
const AdminExpenseFillByAcc = lazy(() => import("../Pages/AdminExpenseFillByAcc"));
const AdminLocalConveyanceVoucherFill = lazy(() => import("../Pages/AdminLocalConveyanceVoucherFill "));
const AccVoucherFill = lazy(() => import("../Pages/AccVoucherFill"));
const AccMultiVoucherFill = lazy(() => import("../Pages/AccMultiVoucherFill"));
const AccountantEditMultiVoucherFill = lazy(() => import("../Pages/AccountantEditMultiVoucherFill"));
const AccountantEditLocalConveyanceVoucherFill = lazy(() => import("../Pages/AccountantEditLocalConveyanceVoucherFill"));
const OfficialFillByAccountant = lazy(() => import("../Pages/OfficialFillByAccountant"));
const Updateaccforadmin = lazy(() => import("../Pages/Updateaccforadmin"));

// ---- Events ---------------------------------------------------------------
const EventCreate = lazy(() => import("../component/EventCreate"));
const AdminEvent = lazy(() => import("../component/AdminEvent"));
const EventCreatedTable = lazy(() => import("../component/EventCreatedTable"));
const UserEvent = lazy(() => import("../Pages/UserEvent"));
const AdminEventList = lazy(() => import("../Pages/AdminEventList"));
const AccEventList = lazy(() => import("../Pages/AccEventList"));
const EditEvent = lazy(() => import("../Pages/EditEvent"));

// ---- Travel ---------------------------------------------------------------
const TravelEventCreate = lazy(() => import("../component/TravelEventCreate"));
const TravelEventVoucher = lazy(() => import("../component/TravelEventVoucher"));
const TravelEvents = lazy(() => import("../Pages/TravelEvents"));
const TravelEventEdit = lazy(() => import("../Pages/TravelEventEdit"));
const TravelExpenseRequest = lazy(() => import("../Pages/TravelExpenseRequest"));
const TravelExpenseRequestAccountant = lazy(() => import("../Pages/TravelExpenseRequestAccountant"));
const TravelVoucherFill = lazy(() => import("../Pages/TravelVoucherFill"));
const TravelUserVoucherDetails = lazy(() => import("../Pages/TravelUserVoucherDetails"));
const TravelExpUserPage = lazy(() => import("../Pages/TravelExpUserPage"));
const TravelExpUserPageaccountant = lazy(() => import("../Pages/TravelExpUserPageaccountant"));
const AccountantTravelVoucherList = lazy(() => import("../Pages/AccountantTravelVoucherList"));
const AdminTravelVoucherFill = lazy(() => import("../Pages/AdminTravelVoucherFill"));
const AdminUpdateTrevelVoucherDetails = lazy(() => import("../Pages/AdminUpdateTrevelVoucherDetails"));
const EditTravelVoucher = lazy(() => import("../Pages/EditTravelVoucher"));

// ---- Drafts ---------------------------------------------------------------
const CreateDraft = lazy(() => import("../Pages/CreateDraft"));
const DraftList = lazy(() => import("../Pages/DraftList"));
const DraftVoucerDetailsFill = lazy(() => import("../Pages/DraftVoucerDetailsFill"));
const AdminDraftVoucherfinal = lazy(() => import("../Pages/AdminDraftVoucherfinal"));

// ---- Approvals ------------------------------------------------------------
const ApprovedList = lazy(() => import("../component/ApprovedList"));
const PendingExpense = lazy(() => import("../component/PendingExpense"));
const PaidExpensesAccountant = lazy(() => import("../Pages/PaidExpensesAccountant"));
const BatchPaymentAccountant = lazy(() => import("../Pages/BatchPaymentAccountant"));
const BatchVoucherListAccountant = lazy(() => import("../Pages/BatchVoucherListAccountant"));

// ---- Projects -------------------------------------------------------------
const ProjectMaster = lazy(() => import("../Pages/ProjectMaster"));
const ProjectList = lazy(() => import("../Pages/ProjectList"));
const ProjectVoucherList = lazy(() => import("../Pages/ProjectVoucherList"));

// ---- Purchase / Sales / Inventory ------------------------------------------
const Purchase = lazy(() => import("../Pages/Purchase"));
const PurchaseList = lazy(() => import("../component/PurchaseList"));
const PurchaseApply = lazy(() => import("../component/PurchaseApply"));
const NewPurchaseEntry = lazy(() => import("../component/NewPurchaseEntry"));
const Sales = lazy(() => import("../Pages/Sales"));
const NewSalesEntry = lazy(() => import("../component/NewSalesEntry"));
const Inventory = lazy(() => import("../Pages/Inventory"));

// ---- Payroll --------------------------------------------------------------
const Salary = lazy(() => import("../Pages/Salary"));
const SalaryDetails = lazy(() => import("../Pages/SalaryDetails"));
const DeductionDetails = lazy(() => import("../Pages/DeductionDetails"));

// ---- Banking --------------------------------------------------------------
const BankStatement = lazy(() => import("../Pages/BankStatement"));
const Reconsilation = lazy(() => import("../Pages/Reconsilation"));

// ---- Analytics ------------------------------------------------------------
const AllAnalytics = lazy(() => import("../Pages/AllAnalytics"));
const AllAppliedAnalytics = lazy(() => import("../Pages/AllAppliedAnalytics"));
const AllAppliedAnalyticsyqm = lazy(() => import("../Pages/AllAppliedAnalyticsyqm"));
const AnalyticsVoucher = lazy(() => import("../Pages/AnalyticsVoucher"));
const AllAnalyticsAfterFilter = lazy(() => import("../component/AllAnalyticsAfterFilter"));

// ---- Masters --------------------------------------------------------------
const Master = lazy(() => import("../Pages/Master"));
const CompanyMaster = lazy(() => import("../Pages/CompanyMaster"));
const BankMaster = lazy(() => import("../Pages/BankMaster"));
const PaymentMaster = lazy(() => import("../Pages/PaymentMaster"));
const ExpenseMaster = lazy(() => import("../Pages/ExpenseMaster"));
const ExpenseSubMaster = lazy(() => import("../Pages/ExpenseSubMaster"));
const LocationMaster = lazy(() => import("../Pages/LocationMaster"));
const InventoryMaster = lazy(() => import("../Pages/InventoryMaster"));

// ---- Users ----------------------------------------------------------------
const UserLists = lazy(() => import("../Pages/UserLists"));
const Registration = lazy(() => import("../Pages/Registration"));
const UserDetails = lazy(() => import("../Pages/UserDetails"));
const UpdateProfileUser = lazy(() => import("../Pages/UpdateProfileUser"));
const EditUserProfile = lazy(() => import("../Pages/EditUserProfile"));
const ProfilePage = lazy(() => import("../Pages/ProfilePage"));

// ---- System (SuperAdmin) ---------------------------------------------------
const SystemDashboard = lazy(() => import("../Pages/system/SystemDashboard"));
const TenantManagement = lazy(() => import("../Pages/system/TenantManagement"));
const RoleManagement = lazy(() => import("../Pages/system/RoleManagement"));
const RolePermissions = lazy(() => import("../Pages/system/RolePermissions"));
const ModuleManagement = lazy(() => import("../Pages/system/ModuleManagement"));
const UserAccess = lazy(() => import("../Pages/system/UserAccess"));
const BrandingSettings = lazy(() => import("../Pages/system/BrandingSettings"));
const CompanyTenants = lazy(() => import("../Pages/system/CompanyTenants"));

export const routes = [
  // Dashboards
  { path: "/Dashboard", component: Dashboard, module: "DASHBOARD_USER" },
  { path: "/DashboardAdmin", component: DashboardAdmin, module: "DASHBOARD_ADMIN" },
  { path: "/DashboardAccount", component: DashboardAccount, module: "DASHBOARD_ACCOUNT" },

  // Expenses
  { path: "/UserVoucherFill", component: UserVoucherFill, module: "EXP_APPLY", permission: "CREATE" },
  { path: "/UserVoucherMulti", component: UserVoucherMulti, module: "EXP_MULTI", permission: "CREATE" },
  { path: "/UserLocalConveyanceVoucher", component: UserLocalConveyanceVoucher, module: "EXP_LOCAL", permission: "CREATE" },
  { path: "/ExpenseDeclaration", component: ExpenseDeclaration, module: "EXP_DECLARATION" },
  { path: "/ExpenseForm", component: ExpenseForm, module: "EXP_APPLY", permission: "CREATE" },
  { path: "/ExpenseRequestAdmin", component: ExpenseRequestAdmin, module: "EXP_REQ_ADMIN" },
  { path: "/ExpenseRequestAdminList", component: ExpenseRequestAdminList, module: "EXP_REQ_ADMIN" },
  { path: "/ExpenseRequestAccountant", component: ExpenseRequestAccountant, module: "EXP_REQ_ACC" },
  { path: "/ExpenseRequestAccountantnew", component: ExpenseRequestAccountantnew, module: "EXP_REQ_ACC" },
  { path: "/ExpenseRequestAccountantbatch", component: ExpenseRequestAccountantbatch, module: "EXP_REQ_ACC" },
  { path: "/ExpenseRequestAccountantbatchlist", component: ExpenseRequestAccountantbatchlist, module: "EXP_REQ_ACC" },
  { path: "/ExpenseRequestHR", component: ExpenseRequestHR, module: "EXP_REQ_HR" },
  { path: "/AdminVoucherEditByUser", component: AdminVoucherEditByUser, module: "EXP_EDIT_BY_USER" },
  { path: "/RevokeEditVoucher", component: RevokeEditVoucher, module: "EXP_REVOKE" },
  { path: "/PendingVoucher", component: PendingVoucher, module: "EXP_PENDING" },
  { path: "/AcceptedVoucher", component: AcceptedVoucher, module: "EXP_ACCEPTED" },
  { path: "/RejectedVoucher", component: RejectedVoucher, module: "EXP_REJECTED" },
  { path: "/CompletedExpense", component: CompletedExpense, module: "EXP_COMPLETED" },
  { path: "/OfficialHistory", component: OfficialHistory, module: "EXP_HISTORY_OFF" },
  { path: "/AdminHistoryOff", component: AdminHistoryOff, module: "EXP_HISTORY_ADMIN" },

  // Voucher detail views - any user who can see a list can open a row from it
  { path: "/VoucherFullDetail/:voucherId", component: VoucherFullDetail, module: "EXP_DETAIL" },
  { path: "/VoucherDetails", component: VoucherDetails, module: "EXP_DETAIL" },
  { path: "/AdminVoucherFullDetail", component: AdminVoucherFullDetail, module: "EXP_DETAIL" },
  { path: "/AccountantVoucherFullDetail", component: AccountantVoucherFullDetail, module: "EXP_DETAIL" },
  { path: "/AdminVoucherDetailsAll", component: AdminVoucherDetailsAll, module: "EXP_DETAIL" },
  { path: "/AdminVoucherHistoryDetails", component: AdminVoucherHistoryDetails, module: "EXP_DETAIL" },
  { path: "/ApprovedAdminVoucherdetails", component: ApprovedAdminVoucherdetails, module: "EXP_DETAIL" },

  // Expense edit / fill forms - gated on the verb they perform
  { path: "/EditVoucher", component: EditVoucher, module: "EXP_APPLY", permission: "EDIT" },
  { path: "/EditMultiVoucher", component: EditMultiVoucher, module: "EXP_MULTI", permission: "EDIT" },
  { path: "/EditLocalConveyanceVoucher", component: EditLocalConveyanceVoucher, module: "EXP_LOCAL", permission: "EDIT" },
  { path: "/EditVoucherAccountant", component: EditVoucherAccountant, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AdminEditVoucher", component: AdminEditVoucher, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminUpdateVoucherDetails", component: AdminUpdateVoucherDetails, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminUpdateMultiVoucher", component: AdminUpdateMultiVoucher, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminUpdateLocalConveyanceVoucher", component: AdminUpdateLocalConveyanceVoucher, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminVoucerDetailsFill", component: AdminVoucerDetailsFill, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminMultiVoucherFill", component: AdminMultiVoucherFill, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminMultiVoucherByAcc", component: AdminMultiVoucherByAcc, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AdminExpenseFillByAcc", component: AdminExpenseFillByAcc, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AdminLocalConveyanceVoucherFill", component: AdminLocalConveyanceVoucherFill, module: "EXP_REQ_ADMIN", permission: "EDIT" },
  { path: "/AccVoucherFill", component: AccVoucherFill, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AccMultiVoucherFill", component: AccMultiVoucherFill, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AccountantEditMultiVoucherFill", component: AccountantEditMultiVoucherFill, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/AccountantEditLocalConveyanceVoucherFill", component: AccountantEditLocalConveyanceVoucherFill, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/OfficialFillbyAccountant", component: OfficialFillByAccountant, module: "EXP_REQ_ACC", permission: "EDIT" },
  { path: "/Updateaccforadmin", component: Updateaccforadmin, module: "EXP_REQ_ACC", permission: "EDIT" },

  // Events
  { path: "/EventCreate", component: EventCreate, module: "EVT_CREATE", permission: "CREATE" },
  { path: "/AdminEvent", component: AdminEvent, module: "EVT_CREATE", permission: "CREATE" },
  { path: "/EventCreatedTable", component: EventCreatedTable, module: "EVT_CREATED_TABLE" },
  { path: "/UserEvent", component: UserEvent, module: "EVT_MY" },
  { path: "/AdminEventList", component: AdminEventList, module: "EVT_ADMIN_LIST" },
  { path: "/AccEventList", component: AccEventList, module: "EVT_ACC_LIST" },
  { path: "/EditEvent", component: EditEvent, module: "EVT_ADMIN_LIST", permission: "EDIT" },

  // Travel
  { path: "/TravelEventCreate", component: TravelEventCreate, module: "TRV_CREATE", permission: "CREATE" },
  { path: "/TravelEventVoucher", component: TravelEventVoucher, module: "TRV_EVENTS" },
  { path: "/TravelEvents", component: TravelEvents, module: "TRV_EVENTS" },
  { path: "/TravelEventEdit", component: TravelEventEdit, module: "TRV_EVENTS", permission: "EDIT" },
  { path: "/TravelExpenseRequest", component: TravelExpenseRequest, module: "TRV_REQ_ADMIN" },
  { path: "/TravelExpenseRequestAccountant", component: TravelExpenseRequestAccountant, module: "TRV_REQ_ACC" },
  { path: "/TravelVoucherFill", component: TravelVoucherFill, module: "TRV_VOUCHER_FILL", permission: "CREATE" },
  { path: "/TravelUserVoucherDetails", component: TravelUserVoucherDetails, module: "TRV_REQ_ADMIN" },
  { path: "/TravelExpUserPage", component: TravelExpUserPage, module: "TRV_REQ_ADMIN" },
  { path: "/TravelExpUserPageaccountant", component: TravelExpUserPageaccountant, module: "TRV_REQ_ACC" },
  { path: "/AccountantTravelVoucherList", component: AccountantTravelVoucherList, module: "TRV_ACC_LIST" },
  { path: "/AdminTravelVoucherFill", component: AdminTravelVoucherFill, module: "TRV_REQ_ADMIN", permission: "EDIT" },
  { path: "/AdminUpdateTrevelVoucherDetails", component: AdminUpdateTrevelVoucherDetails, module: "TRV_REQ_ADMIN", permission: "EDIT" },
  { path: "/EditTravelVoucher", component: EditTravelVoucher, module: "TRV_VOUCHER_FILL", permission: "EDIT" },

  // Drafts
  { path: "/CreateDraft", component: CreateDraft, module: "DRF_CREATE", permission: "CREATE" },
  { path: "/DraftList", component: DraftList, module: "DRF_LIST" },
  { path: "/DraftVoucerDetailsFill", component: DraftVoucerDetailsFill, module: "DRF_LIST", permission: "EDIT" },
  { path: "/AdminDraftVoucherfinal", component: AdminDraftVoucherfinal, module: "DRF_LIST", permission: "EDIT" },

  // Approvals
  { path: "/ApprovedList", component: ApprovedList, module: "APR_APPROVED_LIST" },
  { path: "/AllAnalytics-PendingExpense", component: PendingExpense, module: "ANL_ALL" },
  { path: "/PaidExpensesAccountant", component: PaidExpensesAccountant, module: "APR_PAID" },
  { path: "/BatchPaymentAccountant", component: BatchPaymentAccountant, module: "APR_BATCH_PAY" },
  { path: "/BatchVoucherListAccountant", component: BatchVoucherListAccountant, module: "APR_BATCH_LIST" },

  // Projects
  { path: "/ProjectMaster", component: ProjectMaster, module: "PRJ_MASTER" },
  { path: "/ProjectList", component: ProjectList, module: "PRJ_LIST" },
  { path: "/ProjectVoucherList/:projectId", component: ProjectVoucherList, module: "PRJ_VOUCHERS" },

  // Purchase / Sales / Inventory
  { path: "/Purchase", component: Purchase, module: "PUR_ENTRY" },
  { path: "/PurchaseList", component: PurchaseList, module: "PUR_LIST" },
  { path: "/purchaseApply", component: PurchaseApply, module: "PUR_ENTRY", permission: "CREATE" },
  { path: "/NewPurchaseEntry", component: NewPurchaseEntry, module: "PUR_NEW", permission: "CREATE" },
  { path: "/Sales", component: Sales, module: "SAL_LIST" },
  { path: "/NewSalesEntry", component: NewSalesEntry, module: "SAL_NEW", permission: "CREATE" },
  { path: "/Inventory", component: Inventory, module: "INV_STOCK" },

  // Payroll
  { path: "/Salary", component: Salary, module: "PAY_SALARY" },
  { path: "/SalaryDetails", component: SalaryDetails, module: "PAY_SALARY_DETAIL" },
  { path: "/DeductionDetails", component: DeductionDetails, module: "PAY_DEDUCTION" },

  // Banking
  { path: "/BankStatement", component: BankStatement, module: "BNK_STATEMENT" },
  { path: "/Reconsilation", component: Reconsilation, module: "BNK_RECONCILE" },

  // Analytics
  { path: "/AllAnalytics", component: AllAnalytics, module: "ANL_ALL" },
  { path: "/AllAnalytics-FilterExpense", component: AllAnalyticsAfterFilter, module: "ANL_ALL" },
  { path: "/AllAppliedAnalytics", component: AllAppliedAnalytics, module: "ANL_APPLIED" },
  { path: "/AllAppliedAnalyticsyqm", component: AllAppliedAnalyticsyqm, module: "ANL_YQM" },
  { path: "/AnalyticsVoucher", component: AnalyticsVoucher, module: "ANL_VOUCHER" },

  // Masters
  { path: "/Master", component: Master, module: "MST_HOME" },
  { path: "/CompanyMaster", component: CompanyMaster, module: "MST_COMPANY" },
  { path: "/BankMaster", component: BankMaster, module: "MST_BANK" },
  { path: "/PaymentMaster", component: PaymentMaster, module: "MST_PAYMENT" },
  { path: "/ExpenseMaster", component: ExpenseMaster, module: "MST_EXPENSE" },
  { path: "/ExpenseSubMaster", component: ExpenseSubMaster, module: "MST_EXPENSE_SUB" },
  { path: "/LocationMaster", component: LocationMaster, module: "MST_LOCATION" },
  { path: "/InventoryMaster", component: InventoryMaster, module: "MST_INVENTORY" },

  // Users
  { path: "/UserLists", component: UserLists, module: "USR_LIST" },
  { path: "/Registration", component: Registration, module: "USR_REGISTER", permission: "CREATE" },
  { path: "/UserDetails", component: UserDetails, module: "USR_DETAILS" },
  { path: "/EditUserProfile", component: EditUserProfile, module: "USR_DETAILS", permission: "EDIT" },

  // Own profile - available to every signed-in user regardless of role
  { path: "/UpdateProfileUser", component: UpdateProfileUser, module: null },
  { path: "/ProfilePage", component: ProfilePage, module: null },

  // System.
  // Tenants / Modules / Branding are provider-only: their modules are flagged
  // is_provider_only, so they never resolve for a tenant user, and the API
  // re-checks with [RequireSuperAdmin].
  // Roles / Permissions / User Access are available to a tenant Admin too, but
  // every query is scoped to their own tenant.
  // The provider's own setup overview. It answered to SYS_ROLES before it had
  // a registry row of its own, which let a tenant Admin open the provider's
  // console page simply by holding the Roles module.
  { path: "/system/dashboard", component: SystemDashboard, module: "SYS_DASHBOARD" },
  { path: "/system/tenants", component: TenantManagement, module: "SYS_TENANTS" },
  { path: "/system/company-tenants", component: CompanyTenants, module: "SYS_COMPANY_MAP" },
  { path: "/system/roles", component: RoleManagement, module: "SYS_ROLES" },
  { path: "/system/permissions", component: RolePermissions, module: "SYS_PERMISSIONS" },
  { path: "/system/modules", component: ModuleManagement, module: "SYS_MODULES" },
  { path: "/system/user-access", component: UserAccess, module: "SYS_USER_ACCESS" },
  { path: "/system/branding", component: BrandingSettings, module: "SYS_BRANDING" },
];

export default routes;
